import { action } from '@ember/object';
import { cancel, later, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { PopupCloseReason } from 'webapp/enums/enums';

import CommonPopupsColorPickerComponent from 'webapp/components/common/popups/color-picker';

import type Popup from 'webapp/models-custom/popup/base';

export interface PopupWrapperBaseArgs {
	model: Popup;
}

export interface NodeEventData {
	node: HTMLElement;
	eventName: string;
	fn: () => void;
}

interface PopupWrapperModalComponentInterface<T> {
	Args: T;
}

export default abstract class PopupWrapperBaseComponent<
	T extends PopupWrapperModalComponentInterface<PopupWrapperBaseArgs>,
> extends Component<T> {
	@service('popup') declare popupService: Service.Popup;

	popupNode: HTMLElement | null = null;
	rootNode: HTMLElement | null = null;

	abstract animateLength: number;
	abstract reposition(): void;

	protected boundEvents: NodeEventData[] = [];

	protected bindEvents(events: NodeEventData[]): void {
		events.forEach((eventData) => {
			eventData.node.addEventListener(eventData.eventName, eventData.fn);

			// LEGACY jquery fallback
			// remove them when getting rid of jquery / trigger events directly
			$(eventData.node).on(eventData.eventName, eventData.fn);

			this.boundEvents.push(eventData);
		});
	}

	protected unbindEvents(): void {
		this.boundEvents.forEach((eventData) => {
			eventData.node.removeEventListener(eventData.eventName, eventData.fn);

			// LEGACY jquery fallback
			// remove them when getting rid of jquery / trigger events directly
			$(eventData.node).off(eventData.eventName, eventData.fn); // jquery fallback
		});
		this.boundEvents = [];
	}

	protected readyToOpen(): void {
		const node = this.popupNode,
			rootNode = this.rootNode,
			autofocus = this.args.model.autofocus;

		if (rootNode && autofocus) {
			// need for popups that dont have autofocus field - so weird
			rootNode.focus();
		}

		if (node) {
			if (this.args.model.isAnimated) {
				// Flush the closed style so 'shown' transitions (reposition() skips layout for drawers/fullscreen).
				void node.offsetWidth;
			}
			node.classList.add('shown');
			this.args.model.triggerEvent('nodeVisibleAfterOpen');
			if (autofocus) {
				later(this, this.focusFirstInput, 200);
			}
		}
	}

	focusFirstInput(): void {
		const node = this.popupNode;

		if (!node) {
			return;
		}

		const allInputs = Array.from(node.getElementsByTagName('input'));
		const firstFocusableInput = allInputs.find((inputNode: HTMLInputElement) => {
			return (
				!inputNode.classList.contains('unfocusable') &&
				inputNode.getAttribute('type') !== 'file' &&
				!inputNode.hidden
			);
		});

		if (firstFocusableInput) {
			// Sometimes, the popup (although with "shown" class) is still classified
			// as hidden visibility / 0 opacity so the input cannot be focused.
			// If focus event is not triggered within 200ms, retry focus (just once)
			let retryTimer: ReturnType<typeof later> | null = null;

			const onFocusFn = () => {
				if (retryTimer) {
					cancel(retryTimer);
					retryTimer = null;
				}
				firstFocusableInput.removeEventListener('focus', onFocusFn);
			};

			firstFocusableInput.addEventListener('focus', onFocusFn);

			firstFocusableInput.focus();

			retryTimer = later(
				this,
				() => {
					if (document.activeElement !== firstFocusableInput) {
						firstFocusableInput.focus();
					}
					retryTimer = null;
					firstFocusableInput.removeEventListener('focus', onFocusFn);
				},
				200,
			);
		}
	}

	readyToClose(): Promise<void> {
		return new Promise((resolve) => {
			const node = this.popupNode;
			if (node) {
				node.classList.remove('shown');
				if (this.args.model.isAnimated) {
					later(() => resolve(), this.animateLength);
				} else {
					schedule('afterRender', () => {
						resolve();
					});
				}
			} else {
				resolve();
			}
		});
	}

	@action
	close(reason?: PopupCloseReason): void {
		void this.args.model.close(reason);
	}

	@action
	handleOverlayClick() {
		if (this.args.model.closeOnOverlayClick) {
			this.close(PopupCloseReason.OUTSIDECLICK);
		}
	}

	@action
	onOutsideClick(e: Event): void {
		if (this.popupService.isClickInReactPopup(e)) {
			return;
		}

		const popup = this.args.model;
		const target = e.target as HTMLElement;
		if (
			target &&
			target.isConnected &&
			!this.popupService.blockCloseOnOutsideClick &&
			!target.classList.contains('modal-overlay') &&
			!target.classList.contains('infolayer') &&
			!target.closest('.period_picker_box') && // don't close anything when clicked in datetime picker
			!target.closest('.tooltipster-content') && // don't close anything when clicked in tooltipster
			!target.closest('.dbx-annotation-label--view') && // don't close anything when clicked on chart annotation view button (special logic in d-chart/renderer/annotations)
			!target.closest('.chromeperfectpixel-overlay-container') && // dev purposes
			!target.closest('.chromeperfectpixel-panel') && // dev purposes
			(popup.componentInnerClass !== CommonPopupsColorPickerComponent ||
				!target.closest('.colorpicker')) // dont close color picker popup if clicked in form::color-picker input
		) {
			// case with select dropdown sticking out of popup & deselecting multiselect items
			const select2Options = target.closest('.select2-results__options');
			if (select2Options) {
				const selectUniqueName = (select2Options.id || '')
					.replace('select2-', '')
					.replace('-pd-results', '');
				if (selectUniqueName) {
					const selectNode = document.querySelector(`[name=${selectUniqueName}]`);
					if (selectNode && this.popupNode?.contains(selectNode)) {
						return;
					}
				}
				return;
			}

			let clickedInLaterPopup = false;

			let targetPopupRootNode = target.closest('.popup');

			if (targetPopupRootNode?.classList.contains('modal-popup')) {
				const closestModalCt = targetPopupRootNode.closest('.modal-ct');
				targetPopupRootNode = closestModalCt;
			}

			if (targetPopupRootNode) {
				let prevPopupRootNode = targetPopupRootNode.previousElementSibling;

				const thisPopupRootNode = this.rootNode;
				while (prevPopupRootNode) {
					if (thisPopupRootNode === prevPopupRootNode) {
						clickedInLaterPopup = true;
						break;
					}
					prevPopupRootNode = prevPopupRootNode.previousElementSibling;
				}
			}

			if (!clickedInLaterPopup) {
				this.close(PopupCloseReason.OUTSIDECLICK);
			}
		}
	}

	@action
	setupView(node: HTMLElement): void {
		this.rootNode = node;
	}

	@action
	teardownView(): void {
		this.unbindEvents();
	}
}
